import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, asset } from "../lib/api";
import { CoverReel } from "../components/CoverReel";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { ArrowRight, Images, Heart } from "lucide-react";

const CHUNK = 12;

export default function Weddings() {
  const [photos, setPhotos] = useState([]);
  const [album, setAlbum] = useState(null);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    document.title = "Weddings — Scott Burton Photography";
    api.get("/photos", { params: { category: "Wedding", portfolio: true } })
      .then(({ data }) => setPhotos([...data].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0))));
  }, []);

  const heroSlides = useMemo(
    () => photos.slice(0, 8).map((p) => ({ id: p.id, src: asset(p.image_url) })),
    [photos]
  );
  const albums = useMemo(() => {
    const out = [];
    for (let i = 0; i < photos.length; i += CHUNK) out.push(photos.slice(i, i + CHUNK));
    return out;
  }, [photos]);

  return (
    <div>
      {/* rotating hero reel */}
      <section className="relative w-full">
        <CoverReel
          variant="center"
          height="75vh"
          icon={Heart}
          overline="Over 1,000 weddings worldwide"
          heading="Scott Burton Photography"
          blurb="Non-intrusive wedding visuals — captured with long lenses, quiet patience and a photojournalist's eye."
          slides={heroSlides}
        />
      </section>

      {/* albums / collages */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline-label mb-2">Wedding collections</div>
            <h2 className="font-serif text-4xl lg:text-5xl font-light">Browse the galleries</h2>
          </div>
          <div className="text-sm text-neutral-400">{photos.length} images · {albums.length} collections</div>
        </div>

        {albums.length === 0 ? (
          <div className="py-24 text-center text-neutral-500">Wedding galleries are loading — check back in a moment.</div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {albums.map((chunk, idx) => (
              <button key={idx} data-testid={`wedding-album-${idx}`} onClick={() => setAlbum(chunk)}
                className="group text-left rounded-lg overflow-hidden border border-white/10 bg-obsidian-800 hover:border-amber-glow/50 transition-colors">
                <div className="grid grid-cols-2 grid-rows-2 gap-0.5 aspect-[4/3]">
                  {chunk.slice(0, 4).map((p) => (
                    <div key={p.id} className="overflow-hidden">
                      <img src={asset(p.image_url)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                    </div>
                  ))}
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-serif text-xl">Collection {String(idx + 1).padStart(2, "0")}</div>
                    <div className="overline-label mt-0.5">{chunk.length} images</div>
                  </div>
                  <span className="flex items-center gap-1 text-sm text-amber-glow opacity-0 group-hover:opacity-100 transition-opacity">
                    <Images size={15} /> View
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* album lightbox */}
      <Dialog open={!!album} onOpenChange={(o) => !o && setAlbum(null)}>
        <DialogContent data-testid="wedding-album-modal" className="max-w-6xl w-[96vw] max-h-[92vh] overflow-y-auto bg-obsidian-800 border-white/10">
          <DialogTitle className="font-serif text-2xl font-light">Wedding gallery</DialogTitle>
          {album && (
            <div className="columns-2 md:columns-3 gap-3 mt-2">
              {album.map((p) => (
                <img key={p.id} src={asset(p.image_url)} alt="" loading="lazy"
                  onClick={() => setZoom(p)}
                  className="mb-3 w-full break-inside-avoid rounded cursor-zoom-in hover:opacity-90 transition-opacity" />
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* single zoom */}
      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent data-testid="wedding-zoom" className="max-w-5xl w-[95vw] bg-black border-white/10 p-2">
          <DialogTitle className="sr-only">Wedding photograph</DialogTitle>
          {zoom && <img src={asset(zoom.image_url)} alt="" className="w-full max-h-[85vh] object-contain" />}
        </DialogContent>
      </Dialog>

      <div className="text-center pb-24">
        <Link to="/portfolio" className="inline-flex items-center gap-2 text-sm text-amber-glow link-underline">
          See sports, portraits & prints in the full portfolio <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  );
}
